import React from "react";
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Card,
  CardActions,
  CardContent,
  Checkbox,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  IconButton,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import copy from "copy-to-clipboard";
import {
  useCreateAdminApiKeyMutation,
  useGetAdminApiKeysQuery,
  useGetAllUsersQuery,
  useRevokeAdminApiKeyMutation,
} from "../../Slices/roomNateApis";
import {
  accountLabel,
  buildAdminApiKeyBody,
  formatKeyExpiry,
  normalizePartnerAddress,
  stripSecret,
  partnerAddressWarning,
  privilegedAccountWarning,
  readApiError,
} from "../../utils/apiKeyProvisioning";

const DEFAULT_DAYS = "7";

function statusColor(status) {
  if (status === "active") return "success";
  if (status === "expired") return "warning";
  return "default";
}

function statusLabel(status) {
  if (status === "active") return "Active";
  if (status === "expired") return "Expired";
  if (status === "revoked") return "Revoked";
  return "Unknown";
}

function CopyField({ label, value }) {
  const [note, setNote] = React.useState("");

  const onCopy = () => {
    const ok = copy(value);
    setNote(ok ? "Copied" : `Could not copy the ${label.toLowerCase()}. Select it and copy it yourself.`);
  };

  return (
    <Box>
      <TextField
        label={label}
        value={value}
        fullWidth
        margin="dense"
        InputProps={{ readOnly: true }}
        inputProps={{ "aria-label": label, autoComplete: "off" }}
      />
      <Stack direction="row" alignItems="center" spacing={1}>
        <Button size="small" startIcon={<ContentCopyIcon />} onClick={onCopy}>
          Copy {label.toLowerCase()}
        </Button>
        {note ? (
          <Typography variant="caption" color="text.secondary">
            {note}
          </Typography>
        ) : null}
      </Stack>
    </Box>
  );
}

function KeyCard({ apiKey, onRevoke }) {
  const name = apiKey?.name || "Untitled key";
  const account =
    apiKey?.user && typeof apiKey.user === "object"
      ? accountLabel(apiKey.user)
      : "Unknown account";
  const origin = apiKey?.metadata?.origin || "No website address";
  const note = apiKey?.metadata?.description || "";
  const status = apiKey?.status || (apiKey?.isActive === false ? "revoked" : "active");
  const canRevoke = status !== "revoked" && apiKey?.isActive !== false;

  return (
    <Card
      elevation={0}
      sx={{ border: "1px solid", borderColor: "divider", borderRadius: 3 }}
    >
      <CardContent sx={{ pb: 1, pt: 1.5, px: 2 }}>
        <Stack direction="row" alignItems="flex-start" justifyContent="space-between" gap={1}>
          <Box sx={{ minWidth: 0 }}>
            <Typography variant="body2" fontWeight={600} sx={{ wordBreak: "break-word" }}>
              {name}
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ display: "block" }}>
              {account}
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ display: "block" }}>
              {origin}
            </Typography>
            {note ? (
              <Typography variant="caption" sx={{ display: "block", mt: 0.5 }}>
                {note}
              </Typography>
            ) : null}
            <Typography
              variant="caption"
              sx={{ display: "block", fontFamily: "monospace", wordBreak: "break-all", mt: 0.5 }}
            >
              {apiKey?.key || "Key unavailable"}
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 0.5 }}>
              {formatKeyExpiry(apiKey?.expiresAt)}
            </Typography>
          </Box>
          <Chip
            label={statusLabel(status)}
            size="small"
            color={statusColor(status)}
            variant={status === "active" ? "filled" : "outlined"}
            sx={{ flexShrink: 0 }}
          />
        </Stack>
      </CardContent>
      <Divider />
      <CardActions sx={{ px: 1.5, py: 0.75 }}>
        {apiKey?.key ? (
          <Tooltip title="Copy key">
            <IconButton
              size="small"
              aria-label={`Copy key for ${name}`}
              onClick={() => copy(apiKey.key)}
            >
              <ContentCopyIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        ) : null}
        {canRevoke ? (
          <Button
            size="small"
            color="error"
            aria-label={`Revoke ${name}`}
            onClick={() => onRevoke(apiKey)}
          >
            Revoke
          </Button>
        ) : null}
      </CardActions>
    </Card>
  );
}

export default function ApiKeyAdmin() {
  const { data: users, isFetching: usersLoading, isError: usersError } = useGetAllUsersQuery();
  const [showRevoked, setShowRevoked] = React.useState(false);
  const {
    data: keyList,
    isFetching: keysLoading,
    isError: keysError,
    error: keysErrorDetail,
  } = useGetAdminApiKeysQuery({ includeInactive: showRevoked });
  const [createAdminApiKey, { isLoading: creating }] = useCreateAdminApiKeyMutation();
  const [revokeAdminApiKey, { isLoading: revoking }] = useRevokeAdminApiKeyMutation();

  const [selectedUser, setSelectedUser] = React.useState(null);
  const [name, setName] = React.useState("");
  const [origin, setOrigin] = React.useState("");
  const [description, setDescription] = React.useState("");
  const [expiresInDays, setExpiresInDays] = React.useState(DEFAULT_DAYS);
  const [errors, setErrors] = React.useState({});
  const [formError, setFormError] = React.useState("");
  const [issued, setIssued] = React.useState(null);
  const [secretSaved, setSecretSaved] = React.useState(false);
  const [pendingRevoke, setPendingRevoke] = React.useState(null);
  const [revokeError, setRevokeError] = React.useState("");
  const submittingRef = React.useRef(false);
  const [submitting, setSubmitting] = React.useState(false);

  const accounts = React.useMemo(() => {
    const list = Array.isArray(users) ? users : [];
    return list
      .filter((user) => user && user._id)
      .sort((a, b) => String(a.email || "").localeCompare(String(b.email || "")));
  }, [users]);

  const normalizedOrigin = normalizePartnerAddress(origin);
  const originWarning = partnerAddressWarning(normalizedOrigin);
  const accountWarning = privilegedAccountWarning(selectedUser);
  const keys = (keyList?.keys || []).map(stripSecret);
  const totalCount = keyList?.totalCount ?? keys.length;

  const originHelper =
    errors.origin ||
    (origin.trim() && normalizedOrigin && normalizedOrigin !== origin.trim().toLowerCase()
      ? `This will be saved as ${normalizedOrigin}.`
      : "Use the embed address, for example serhant.magicfurnish.com.");

  const onCreate = async (event) => {
    event.preventDefault();
    if (submittingRef.current) return;
    setFormError("");
    const built = buildAdminApiKeyBody({
      userId: selectedUser?._id,
      name,
      origin,
      expiresInDays,
      description,
    });
    if (!built.ok) {
      setErrors(built.errors);
      return;
    }
    setErrors({});
    submittingRef.current = true;
    setSubmitting(true);
    try {
      const result = await createAdminApiKey(built.body);
      if (result.error) {
        setFormError(readApiError(result.error, "Could not create the key."));
        return;
      }
      const created = result.data?.data;
      if (!created?.key || !created?.secret) {
        setFormError(
          "The key was created, but the secret did not come back. Revoke it and create a new one."
        );
        return;
      }
      setSecretSaved(false);
      setIssued(created);
      setName("");
      setDescription("");
    } finally {
      submittingRef.current = false;
      setSubmitting(false);
    }
  };

  const closeIssued = () => {
    if (!secretSaved) return;
    setIssued(null);
    setSecretSaved(false);
  };

  const confirmRevoke = async () => {
    if (!pendingRevoke?._id) return;
    setRevokeError("");
    const result = await revokeAdminApiKey(pendingRevoke._id);
    if (result.error) {
      setRevokeError(readApiError(result.error, "Could not revoke the key."));
      return;
    }
    setPendingRevoke(null);
  };

  return (
    <Box sx={{ maxWidth: 720 }}>
      <Typography variant="h6" fontWeight={700} sx={{ mb: 0.5 }}>
        API keys
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Create a key so a partner system can look up a floor plan that already exists.
        The secret is shown once, on the next screen. Copy it before you close that screen.
      </Typography>

      <Box component="form" onSubmit={onCreate} sx={{ mb: 4 }}>
        <Stack spacing={2}>
          <Autocomplete
            options={accounts}
            value={selectedUser}
            onChange={(_, value) => {
              setSelectedUser(value);
              setErrors((current) => ({ ...current, userId: undefined }));
            }}
            getOptionLabel={(user) => accountLabel(user)}
            isOptionEqualToValue={(a, b) => a?._id === b?._id}
            loading={usersLoading}
            disabled={usersLoading || usersError}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Account"
                required
                error={Boolean(errors.userId)}
                helperText={
                  usersError
                    ? "Could not load accounts."
                    : errors.userId || "The key acts as this account."
                }
              />
            )}
          />
          {accountWarning ? <Alert severity="warning">{accountWarning}</Alert> : null}

          <TextField
            label="Name"
            value={name}
            required
            onChange={(event) => {
              setName(event.target.value);
              setErrors((current) => ({ ...current, name: undefined }));
            }}
            error={Boolean(errors.name)}
            helperText={errors.name || "A name you will recognize later, such as Serhant lookup."}
          />

          <TextField
            label="Website address"
            value={origin}
            required
            placeholder="serhant.magicfurnish.com"
            onChange={(event) => {
              setOrigin(event.target.value);
              setErrors((current) => ({ ...current, origin: undefined }));
            }}
            error={Boolean(errors.origin)}
            helperText={originHelper}
          />
          {originWarning ? <Alert severity="warning">{originWarning}</Alert> : null}

          <TextField
            label="Note (optional)"
            value={description}
            placeholder="Who this key is for, and why"
            onChange={(event) => {
              setDescription(event.target.value);
              setErrors((current) => ({ ...current, description: undefined }));
            }}
            error={Boolean(errors.description)}
            helperText={
              errors.description ||
              "Shown next to the key in the list, so you can tell later who it belongs to."
            }
          />

          <TextField
            label="Expires in (days)"
            type="number"
            value={expiresInDays}
            required
            inputProps={{ min: 1, max: 3650, step: 1, "aria-label": "Expires in days" }}
            onChange={(event) => {
              setExpiresInDays(event.target.value);
              setErrors((current) => ({ ...current, expiresInDays: undefined }));
            }}
            error={Boolean(errors.expiresInDays)}
            helperText={errors.expiresInDays || "The key stops working after this many days."}
          />

          <Typography variant="body2" color="text.secondary">
            This key can only look up a floor plan that already exists. It cannot upload a new one.
          </Typography>

          {formError ? <Alert severity="error">{formError}</Alert> : null}

          <Box>
            <Button type="submit" variant="contained" disabled={submitting || creating || usersLoading}>
              {submitting || creating ? "Creating…" : "Create key"}
            </Button>
          </Box>
        </Stack>
      </Box>

      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1.5 }}>
        <Typography variant="h6" fontWeight={700}>
          Existing keys
          {totalCount > 0 ? (
            <Typography component="span" variant="body2" color="text.secondary">
              {" "}
              ({totalCount})
            </Typography>
          ) : null}
        </Typography>
        <FormControlLabel
          control={
            <Checkbox
              checked={showRevoked}
              onChange={(event) => setShowRevoked(event.target.checked)}
            />
          }
          label="Show revoked keys"
        />
      </Stack>

      {keysError ? (
        <Alert severity="error" sx={{ mb: 2 }}>
          {readApiError(keysErrorDetail, "Could not load keys.")}
        </Alert>
      ) : null}
      {keysLoading && keys.length === 0 ? (
        <Typography color="text.secondary">Loading keys…</Typography>
      ) : null}
      {!keysLoading && !keysError && keys.length === 0 ? (
        <Typography color="text.secondary">No keys yet.</Typography>
      ) : null}
      {totalCount > keys.length ? (
        <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 1 }}>
          Showing the most recent {keys.length} of {totalCount}.
        </Typography>
      ) : null}
      <Stack spacing={1.5}>
        {keys.map((apiKey) => (
          <KeyCard key={apiKey._id || apiKey.key} apiKey={apiKey} onRevoke={setPendingRevoke} />
        ))}
      </Stack>

      <Dialog
        open={Boolean(issued)}
        onClose={(_, reason) => {
          if (reason === "backdropClick" || reason === "escapeKeyDown") return;
        }}
        aria-labelledby="api-key-secret-title"
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle id="api-key-secret-title">Save this secret</DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mb: 2 }}>
            This secret is shown once. After you close this, it cannot be recovered.
          </Alert>
          {issued ? (
            <>
              <CopyField label="Key" value={issued.key || ""} />
              <CopyField label="Secret" value={issued.secret || ""} />
              <Typography variant="body2" sx={{ mt: 2 }}>
                Website address: {issued.metadata?.origin || "not set"}
              </Typography>
              <Typography variant="body2">
                Account: {issued.user?.email || accountLabel(selectedUser)}
              </Typography>
            </>
          ) : null}
          <FormControlLabel
            sx={{ mt: 1 }}
            control={
              <Checkbox
                checked={secretSaved}
                onChange={(event) => setSecretSaved(event.target.checked)}
              />
            }
            label="I have copied the secret and stored it somewhere safe"
          />
        </DialogContent>
        <DialogActions>
          <Button variant="contained" disabled={!secretSaved} onClick={closeIssued}>
            Done
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={Boolean(pendingRevoke)}
        onClose={() => {
          if (!revoking) setPendingRevoke(null);
        }}
        aria-labelledby="revoke-api-key-title"
      >
        <DialogTitle id="revoke-api-key-title">Revoke this key?</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            {pendingRevoke?.name || "This key"} will stop working immediately. This cannot be
            undone, because the secret cannot be shown again.
          </Typography>
          {revokeError ? (
            <Alert severity="error" sx={{ mt: 2 }}>
              {revokeError}
            </Alert>
          ) : null}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPendingRevoke(null)} disabled={revoking}>
            Cancel
          </Button>
          <Button color="error" variant="contained" onClick={confirmRevoke} disabled={revoking}>
            {revoking ? "Revoking…" : "Revoke key"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
